export interface ScheduledReportTypeOption {
  value: string;
  label: string;
  description: string;
  delivery: 'pdf' | 'alert' | 'legacy';
  group: 'time-reports' | 'alerts';
  periodMode?: 'single_day' | 'day_of_month_range' | 'iso_week';
}

export const SCHEDULED_REPORT_TYPES: ScheduledReportTypeOption[] = [
  {
    value: 'time-records',
    label: 'Time Records Report',
    description: 'Detailed time entries with filtering',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'daily-summary',
    label: 'Daily Summary Report',
    description: 'Daily overview of employee attendance and work hours',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'campaign-performance',
    label: 'Campaign Performance',
    description: 'Performance metrics and analytics by campaign',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'employee-attendance',
    label: 'Employee Attendance',
    description: 'Comprehensive employee attendance tracking and patterns',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'employee-time-sheet',
    label: 'Employee Time Sheet',
    description: 'Worked hours, absences, tardies, and early departures',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'weekly-attendance',
    label: 'Weekly Attendance Report',
    description: 'Weekly timesheets grouped by employee and week',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'weekly-staffing-coverage',
    label: 'Weekly Staffing & Coverage Report',
    description: 'Workforce activity for customer billing — schedules, attendance, coverage',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'absence',
    label: 'Absence Report',
    description: 'Employee absences, sick leave, and vacation requests',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'tardies',
    label: 'Tardies Report',
    description: 'Late arrivals and punctuality vs scheduled clock-in',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'time-entry-validation',
    label: 'Time Entry Validation',
    description: 'Missing punches and out-of-sequence time entries',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'scheduled-employees',
    label: 'Scheduled Employees Report',
    description: 'Employees scheduled to work and their attendance status',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'missing-punches',
    label: 'Missing Punches Report',
    description: 'Missing clock outs, break ends, or lunch ends',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'idle-summary',
    label: 'Idle Summary Report',
    description: 'Employee activity and idle time by device and date',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'device-uptime',
    label: 'Device Uptime Report',
    description: 'Device connectivity, agent status, and heartbeat timestamps',
    delivery: 'pdf',
    group: 'time-reports',
  },
  {
    value: 'daily_staffing_alert',
    label: 'Attendance Executive Alert',
    description: 'Manager alert — exceptions only (absent, late, uncovered, coverage). Email HTML / SMS, not PDF.',
    delivery: 'alert',
    group: 'alerts',
  },
  {
    value: 'manual-time-adjustments',
    label: 'Manual Time Record Adjustments',
    description: 'HR digest of manual time adds/edits/deletes with impact and pending review counts. Email / SMS.',
    delivery: 'alert',
    group: 'alerts',
    periodMode: 'single_day',
  },
];

export function getScheduledReportLabel(reportType: string): string {
  const legacyLabels: Record<string, string> = {
    morning_attendance_report: 'Morning Attendance Report',
    midday_coverage_report: 'Midday Coverage Report',
    end_of_day_coverage_report: 'End of Day Coverage Report',
  };
  return SCHEDULED_REPORT_TYPES.find((r) => r.value === reportType)?.label
    || legacyLabels[reportType]
    || reportType.replace(/_/g, ' ').replace(/-/g, ' ');
}

export function isDailyStaffingAlertReport(reportType: string): boolean {
  return reportType === 'daily_staffing_alert';
}

export function isManualTimeAdjustmentsReport(reportType: string): boolean {
  return reportType === 'manual-time-adjustments';
}

export function isPdfScheduledReport(reportType: string): boolean {
  const match = SCHEDULED_REPORT_TYPES.find((r) => r.value === reportType);
  return match?.delivery === 'pdf';
}

export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
] as const;

const SINGLE_DAY_REPORTS = new Set([
  'daily-summary',
  'scheduled-employees',
  'daily_staffing_alert',
  'manual-time-adjustments',
  'missing-punches',
]);

const ISO_WEEK_REPORTS = new Set([
  'weekly-attendance',
  'weekly-staffing-coverage',
]);

export function getReportPeriodMode(reportType: string): 'single_day' | 'day_of_month_range' | 'iso_week' {
  if (ISO_WEEK_REPORTS.has(reportType)) return 'iso_week';
  if (SINGLE_DAY_REPORTS.has(reportType)) return 'single_day';
  return 'day_of_month_range';
}

export function reportRequiresDateRange(reportType: string): boolean {
  return getReportPeriodMode(reportType) === 'day_of_month_range';
}

export function reportUsesIsoWeek(reportType: string): boolean {
  return getReportPeriodMode(reportType) === 'iso_week';
}

export const DAY_OF_MONTH_OPTIONS = [
  ...Array.from({ length: 31 }, (_, index) => ({ value: index + 1, label: String(index + 1) })),
  { value: -1, label: 'Last day of month' },
];
